import APIs from '../../api'
import { apiClient } from '../../api/client'

// GET /api/devices/sensors?page=1&limit=10&orderBy=createdAt&order=ASC
// Response: { success, message, data: [{ id, uuid, name, unit, slug }], meta: {...} }
const listSensorTypes = ({ page = 1, limit = 10, search = '', ...rest } = {}) => {
  const params = { page, limit, orderBy: 'createdAt', order: 'ASC' }
  if (search) params.search = search
  Object.assign(params, rest)
  return apiClient.get(APIs.SENSORS.list, { params })
}

// CustomAutoSelect's API-driven mode expects `fetchOptions` to return the
// JSON body (so it can read `data` and `meta.totalPages`). axios wraps the
// JSON inside `response.data`, so we unwrap here.
const fetchSensorTypes = async (opts) => {
  const res = await listSensorTypes(opts)
  return res.data
}

// GET /api/devices/manufacturers?page=1&limit=10&orderBy=name&order=ASC
// Response: { success, message, data: [{ id, uuid, name }], meta: {...} }
const listManufacturers = ({ page = 1, limit = 10, search = '', ...rest } = {}) => {
  const params = { page, limit, orderBy: 'name', order: 'ASC' }
  if (search) params.search = search
  Object.assign(params, rest)
  return apiClient.get(APIs.MANUFACTURERS.list, { params })
}

const fetchManufacturers = async (opts) => {
  const res = await listManufacturers(opts)
  return res.data
}

// GET /api/devices?page=1&limit=10&status=ACTIVE&manufacturerId=&orderBy=createdAt&order=DESC
// Response: { success, message, data: [{ id, uuid, serialNumber, name, manufacturer,
//            sensors: [...] }], meta: {...} }
const listDevices = ({ page = 1, limit = 10, search = '', manufacturerId, ...rest } = {}) => {
  const params = { page, limit, status: 'ACTIVE', orderBy: 'createdAt', order: 'DESC' }
  if (search) params.search = search
  if (manufacturerId) params.manufacturerId = manufacturerId
  Object.assign(params, rest)
  return apiClient.get(APIs.DEVICES.list, { params })
}

const fetchDevices = async (opts) => {
  const res = await listDevices(opts)
  return res.data
}

// GET /api/external-modules?page&limit&manufacturerId&orderBy&order
// Response: { success, message, data: [{ uuid, probeName, serialNumber, ... }], meta }.
// External modules identify themselves by `uuid` (no numeric id in the
// payload), so the caller is expected to use `uuid` as the list key.
const listExternalModules = ({
  page = 1,
  limit = 10,
  search = '',
  manufacturerId,
  ...rest
} = {}) => {
  const params = { page, limit, orderBy: 'createdAt', order: 'DESC' }
  if (search) params.search = search
  if (manufacturerId) params.manufacturerId = manufacturerId
  Object.assign(params, rest)
  return apiClient.get(APIs.EXTERNAL_MODULES.list, { params })
}

const fetchExternalModules = async (opts) => {
  const res = await listExternalModules(opts)
  return res.data
}

// GET /api/input-modules?page&limit&manufacturerId&orderBy&order
// Response: { success, message, data: [{ uuid, name, serialNumber, ... }], meta }.
// Same uuid-only identification as external modules.
const listInputModules = ({ page = 1, limit = 10, search = '', manufacturerId, ...rest } = {}) => {
  const params = { page, limit, orderBy: 'createdAt', order: 'DESC' }
  if (search) params.search = search
  if (manufacturerId) params.manufacturerId = manufacturerId
  Object.assign(params, rest)
  return apiClient.get(APIs.INPUT_MODULES.list, { params })
}

const fetchInputModules = async (opts) => {
  const res = await listInputModules(opts)
  return res.data
}

// GET /api/guardian-users?page=1&limit=10&search=&sortBy=email&direction=ASC
// Response wraps `{ status, data: { data: [...], meta: { total, ... } } }`.
const listGuardianUsers = ({ page = 1, limit = 10, search = '', ...rest } = {}) => {
  const params = { page, limit, sortBy: 'email', direction: 'ASC' }
  if (search) params.search = search
  Object.assign(params, rest)
  return apiClient.get(APIs.GUARDIAN_USERS.list, { params })
}

const fetchGuardianUsers = async (opts) => {
  const res = await listGuardianUsers(opts)
  // Backend wraps the list in two layers; unwrap to the array.
  const payload = res?.data
  const list = Array.isArray(payload?.data?.data)
    ? payload.data.data
    : Array.isArray(payload?.data)
      ? payload.data
      : []
  return { ...payload, data: list }
}

// POST /api/alarm-profiles — body shape varies by alarmType / sensor subtype.
// Caller is responsible for assembling the payload; this fn just sends it.
const createAlarmProfile = (payload) => apiClient.post(APIs.ALARM_PROFILES.create, payload)

// GET /api/alarm-profiles?page&limit&name&alarmType&eventName&sensorId&deviceId&inputModuleId&externalModuleId&uuid&orderBy&order
// Allowed orderBy: id | uuid | name | alarmType | eventName | sensorId | companyId | createdAt | updatedAt
// Allowed order:   ASC | DESC (default DESC)
//
// Note: do NOT spread `...rest` — earlier the caller's signature included a
// `search` field that leaked into the URL. We now whitelist every parameter
// explicitly so `deviceId` always sends as `?deviceId=<id>` (never `search`).
const listAlarmProfiles = ({
  page = 1,
  limit = 10,
  name,
  alarmType,
  eventName,
  sensorId,
  deviceId,
  inputModuleId,
  externalModuleId,
  uuid,
  orderBy = 'createdAt',
  order = 'DESC',
} = {}) => {
  const params = { page, limit, orderBy, order }
  // Whitelist set — drop anything not in this list, even if the caller passes
  // extra fields. Numeric ids must be serialised as strings.
  if (name != null && name !== '') params.name = name
  if (alarmType) params.alarmType = alarmType
  if (eventName) params.eventName = eventName
  if (sensorId !== '' && sensorId != null) params.sensorId = sensorId
  if (deviceId !== '' && deviceId != null) params.deviceId = deviceId
  if (inputModuleId !== '' && inputModuleId != null) params.inputModuleId = inputModuleId
  if (externalModuleId !== '' && externalModuleId != null)
    params.externalModuleId = externalModuleId
  if (uuid) params.uuid = uuid
  // eslint-disable-next-line no-console
  console.log('[alarm-profile list]', APIs.ALARM_PROFILES.list, params)
  return apiClient.get(APIs.ALARM_PROFILES.list, { params })
}

// GET /api/alarm-profiles/:uuid — full DTO with profiles, notificationRules, recipients.
const getAlarmProfile = (uuid) => apiClient.get(APIs.ALARM_PROFILES.byUuid(uuid))

// PUT /api/alarm-profiles/:uuid — partial update. Same nested-shape as the
// create payload (profiles, notificationRules, devices, inputModules,
// externalModules all use full-replace semantics). Caller assembles the
// payload the same way it does for create.
const updateAlarmProfile = (uuid, payload) =>
  apiClient.put(APIs.ALARM_PROFILES.byUuid(uuid), payload)

// POST /api/alarm-profiles/:uuid/duplicate — server creates a fresh copy
// (`name = Copy of <source> (copy N)`, status ACTIVE) carrying profiles +
// rules + recipients + devices + inputModules + externalModules. Returns the
// new template in the same shape as GET /alarm-profiles/:uuid.
const duplicateAlarmProfile = (uuid) =>
  apiClient.post(`${APIs.ALARM_PROFILES.byUuid(uuid)}/duplicate`)

// DELETE /api/alarm-profiles/:uuid
const deleteAlarmProfile = (uuid) => apiClient.delete(APIs.ALARM_PROFILES.byUuid(uuid))

// DELETE /api/alarm-profiles/bulk-delete with { uuids: [...] } in the body.
// Axios carries the request body on DELETE via the `data` field.
const bulkDeleteAlarmProfiles = (uuids) =>
  apiClient.delete(`${APIs.ALARM_PROFILES.list}/bulk-delete`, { data: { uuids } })

// GET /api/alarm-profiles/export?columns=...&orderBy=...&order=...&<filters>
// Returns a `text/csv` blob. Caller is responsible for turning the response
// into a Blob + download link. `columns` is sent as a repeatable `columns`
// query param (one per value) so the backend header rule `columns` accepts
// an array of column names.
const exportAlarmProfiles = ({
  columns,
  name,
  alarmType,
  eventName,
  sensorId,
  deviceId,
  inputModuleId,
  externalModuleId,
  orderBy = 'createdAt',
  order = 'DESC',
} = {}) => {
  const params = new URLSearchParams()
  params.set('orderBy', orderBy)
  params.set('order', order)
  if (Array.isArray(columns) && columns.length) {
    for (const c of columns) params.append('columns', c)
  }
  if (name != null && name !== '') params.set('name', name)
  if (alarmType) params.set('alarmType', alarmType)
  if (eventName) params.set('eventName', eventName)
  if (sensorId !== '' && sensorId != null) params.set('sensorId', sensorId)
  if (deviceId !== '' && deviceId != null) params.set('deviceId', deviceId)
  if (inputModuleId !== '' && inputModuleId != null) params.set('inputModuleId', inputModuleId)
  if (externalModuleId !== '' && externalModuleId != null)
    params.set('externalModuleId', externalModuleId)
  return apiClient.get(`${APIs.ALARM_PROFILES.export}?${params.toString()}`, {
    responseType: 'blob',
  })
}

// GET /alarm-profile-log?page=1&limit=10&coldRoomId&severity&status&orderBy&order&readFilter
// Paginated, sortable stream of triggered alarm events.
//
// `branch-id` + `platform` headers are injected by the apiClient
// interceptor, so the endpoint is implicitly scoped to the active
// branch. Callers may pass `severity` (CRITICAL / WARNING / INFO / OFFLINE),
// `status` (ACTIVE / RESOLVED), `coldRoomId`, `deviceId`, `isArchive`
// (true → Archive tab, false → Active tab, omit → server default), plus
// any other filter the backend supports — extra keys are forwarded
// verbatim into the query string.
//
// Response envelope: { success, message, data: AlarmProfileLogRow[],
// meta: { total, page, limit, totalPages, all, critical, warning,
// confirmed, unconfirmed } }. Each row carries `currentValue`
// (sensorSlug + sensorValue), `alarmProfileDetails` (severity +
// alarmProfileName + alarmType), `deviceDetails` (serialNumber +
// manufacturer), plus `status`, `duration`, `resolvedAt`, `createdAt`,
// `isRead`, `readAt`, `readBy`.
const listAlarmLogEntries = ({
  page = 1,
  limit = 10,
  severity,
  status,
  coldRoomId,
  deviceId,
  alarmProfileTemplateId,
  readFilter,
  isArchive,
  search,
  orderBy = 'createdAt',
  order = 'DESC',
  ...rest
} = {}) => {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 10))
  const params = { page, limit: safeLimit, orderBy, order }
  if (severity) params.severity = severity
  if (status) params.status = status
  if (coldRoomId !== '' && coldRoomId != null) params.coldRoomId = coldRoomId
  if (deviceId !== '' && deviceId != null) params.deviceId = deviceId
  if (alarmProfileTemplateId !== '' && alarmProfileTemplateId != null)
    params.alarmProfileTemplateId = alarmProfileTemplateId
  // `readFilter` accepts UNCONFIRMED / CONFIRMED; only forward when the
  // caller passes a real value so an empty string doesn't trip the
  // backend into a "filter on ''" no-match.
  if (readFilter === 'UNCONFIRMED' || readFilter === 'CONFIRMED') params.readFilter = readFilter
  if (isArchive === true || isArchive === false) params.isArchive = isArchive
  if (search) params.search = search
  Object.assign(params, rest)
  return apiClient.get(APIs.ALARM_PROFILE_LOGS.list, { params })
}

// GET /cold-rooms?page=1&limit=100 — powers the cold-room dropdown on
// the alarms-log filter modal. Backend returns paged cold-room rows
// with numeric `id` + `name`. We cap at 100 (API limit) since the
// modal only ever needs a single page worth.
const listColdRooms = ({ page = 1, limit = 100, search, ...rest } = {}) => {
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 100))
  const params = { page, limit: safeLimit }
  if (search) params.search = search
  Object.assign(params, rest)
  return apiClient.get(APIs.COLD_ROOMS.list, { params })
}

// GET /alarm-profile-log/:uuid — full single-alarm DTO including the
// `alarmLogTimeline` ladder and `recipientsEmails` envelope. Powers the
// alarm-log details page.
//
// Response envelope: { success, message, data: { uuid, id, source, ...,
// alarmLogTimeline: [...], recipientsEmails: [...] } }.
const getAlarmProfileLog = (uuid) => apiClient.get(APIs.ALARM_PROFILE_LOGS.byUuid(uuid))

// POST /alarm-profile-log/user-actions — append a user-action note to a
// single alarm log row. Body: { alarmProfileLogId, status, note }.
// Caller is responsible for assembling the payload (the alarmProfileLogId
// is the numeric `id` from the detail DTO, status is the uppercased note
// type key like REMARK / CONFIRMATION / CORRECTIVE / RCA / RESUMED).
// Returns the server response unchanged — the caller typically follows up
// by invalidating the alarm-profile-log + timeline caches so the new
// entry shows up in the ladder + the notes panel.
const createAlarmLogUserAction = (payload) =>
  apiClient.post(APIs.ALARM_PROFILE_LOGS.userActions, payload)

// GET /alarm-profile-log/timeline?alarmProfileLogId=<id>&page&limit&orderBy=createdAt&order=ASC
// Timeline of state transitions for a single alarm log row — used by
// the "Alarm Processes" tab's detail dialog to render the ladder of
// severity escalations / acknowledgements / resolutions on top of
// the captured reading.
//
// Response envelope: { success, message, data: AlarmLogTimelineEntry[],
// meta: { total, page, limit, totalPages } }. Each entry carries
// `action` (e.g. `severity_escalated`, `resolved`), `actionDescription`,
// `updatedBy`, `createdAt`, `updatedAt`.
const fetchAlarmLogTimeline = ({
  alarmProfileLogId,
  page = 1,
  limit = 50,
  orderBy = 'createdAt',
  order = 'ASC',
} = {}) => {
  return apiClient.get(APIs.ALARM_PROFILE_LOGS.timeline, {
    params: {
      alarmProfileLogId,
      page,
      limit,
      orderBy,
      order,
    },
  })
}

// PATCH /alarm-profile-log/:uuid/read — flip a single alarm log row's
// `isRead` flag. Drives the inbox dropdown's per-row "Mark as read"
// action; once the response lands, the row drops out of the
// `readFilter=UNCONFIRMED` slice on the next refetch (and the bell
// badge / tab pill counts decrement because `meta.unconfirmed` ticks
// down server-side).
const markAlarmLogRead = (uuid) => apiClient.patch(APIs.ALARM_PROFILE_LOGS.markRead(uuid))

// GET /alarm-profile-log/export?columns=...&<filters>&orderBy&order
// Streams a `text/csv` blob honouring the same filter set as the list
// endpoint (`severity`, `status`, `coldRoomId`, `deviceId`,
// `facilityId`, `isArchive`, `search`, ...). The backend header rule
// reads `columns` as a repeatable query param, so we serialise via
// `URLSearchParams` (axios' `params` serializer flattens arrays into
// `?columns[]=foo` which the rule won't match).
//
// Caller turns the response into a Blob + download link. `columns`
// defaults to the full projected set the UI shows in the table —
// callers only need to pass filter / sort args.
const exportAlarmLogEntries = ({
  columns,
  severity,
  status,
  coldRoomId,
  deviceId,
  facilityId,
  isArchive,
  search,
  orderBy = 'createdAt',
  order = 'DESC',
} = {}) => {
  const params = new URLSearchParams()
  params.set('orderBy', orderBy)
  params.set('order', order)
  if (Array.isArray(columns) && columns.length) {
    for (const c of columns) params.append('columns', c)
  }
  if (severity) params.set('severity', severity)
  if (status) params.set('status', status)
  if (coldRoomId !== '' && coldRoomId != null) params.set('coldRoomId', coldRoomId)
  if (deviceId !== '' && deviceId != null) params.set('deviceId', deviceId)
  if (facilityId !== '' && facilityId != null) params.set('facilityId', facilityId)
  if (isArchive === true || isArchive === false) params.set('isArchive', String(isArchive))
  if (search) params.set('search', search)
  return apiClient.get(`${APIs.ALARM_PROFILE_LOGS.export}?${params.toString()}`, {
    responseType: 'blob',
  })
}

// PATCH /alarm-profile-log/:uuid/archive — flip the row's `isArchive`
// flag to true. Server returns the updated DTO; caller is responsible
// for invalidating the list cache so the row drops out of the Active
// tab and surfaces under Archive on the next refetch.
//
// Body shape: { userNote, status }. `status` is a server enum that
// classifies the archive reason (CONFIRMATION / FALSE_POSITIVE /
// RESOLVED / OTHER). `userNote` is the operator's free-text context
// that lands on the alarm's user-action timeline alongside the
// archive flag flip. Both fields are sent verbatim — the backend
// rejects empty `userNote` for most statuses.
const archiveAlarmLogEntry = (uuid, payload = {}) =>
  apiClient.patch(APIs.ALARM_PROFILE_LOGS.archive(uuid), payload)

export {
  listSensorTypes,
  fetchSensorTypes,
  listManufacturers,
  fetchManufacturers,
  listDevices,
  fetchDevices,
  listExternalModules,
  fetchExternalModules,
  listInputModules,
  fetchInputModules,
  listGuardianUsers,
  fetchGuardianUsers,
  createAlarmProfile,
  listAlarmProfiles,
  getAlarmProfile,
  updateAlarmProfile,
  duplicateAlarmProfile,
  deleteAlarmProfile,
  bulkDeleteAlarmProfiles,
  exportAlarmProfiles,
  listAlarmLogEntries,
  fetchAlarmLogTimeline,
  markAlarmLogRead,
  getAlarmProfileLog,
  createAlarmLogUserAction,
  exportAlarmLogEntries,
  archiveAlarmLogEntry,
  listColdRooms,
}
